import type {
  CostAllocation,
  CounterOffer,
  Deal,
  DealSigner,
  DealSnapshot,
  Deadline,
  Document,
  ExecutionAuditResult,
  KnownDefectIssueResult,
  ClosingMilestone,
  EarnestMoneyResult,
  EscrowInstructionsResult,
  FairHousingReviewResult,
  FairHousingTemplate,
  FinalWalkthroughResult,
  Inspection,
  LoanTracking,
  MilestoneResult,
  Party,
  PartyResolveResult,
  PerformanceNotice,
  PostClosePackageResult,
  ProofOfFundsResult,
  Notification,
  PortalLink,
  PortalStatus,
  RepairRequest,
  RepositoryDocument,
  SnapshotParty,
  Task,
  TaskUpdateInput,
  TenantRemovalBlockedResult,
  TenantRemovalResult,
  User,
  WireInstruction,
} from './types'

export interface DocumentLayoutField {
  id: string
  type: string
  page?: number
  x?: number
  y?: number
  w?: number
  h?: number
  bind?: string
  role?: string
  label?: string
}

export interface DocumentLayoutFieldMap {
  form_code?: string
  version?: number
  fields: DocumentLayoutField[]
}

export interface DocumentLayout {
  id: string
  form_code: string
  version: number
  name: string
  is_active: boolean
  content_type: string
  storage_key: string
  field_map: DocumentLayoutFieldMap
  checksum?: string
  uploaded_by?: string
  created_at: string
  updated_at: string
}

const BASE = '/api'

type DealWritePayload = Partial<Deal> & { negotiated_days?: Record<string, number> }
type CounterOfferWritePayload = Partial<CounterOffer> & { response_deadline?: string }

function token(): string | null {
  return localStorage.getItem('magen_token')
}

function headers(extra?: Record<string, string>): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
    ...extra,
  }
}

function clearAuth(): void {
  localStorage.removeItem('magen_token')
  localStorage.removeItem('magen_refresh_token')
}

let refreshPromise: Promise<boolean> | null = null

async function tryRefresh(): Promise<boolean> {
  const refreshToken = localStorage.getItem('magen_refresh_token')
  if (!refreshToken) return false

  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      const res = await fetch(BASE + '/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      })
      if (!res.ok) {
        clearAuth()
        return false
      }
      const data = (await res.json()) as { access_token: string; refresh_token?: string }
      localStorage.setItem('magen_token', data.access_token)
      if (data.refresh_token) {
        localStorage.setItem('magen_refresh_token', data.refresh_token)
      }
      return true
    } catch {
      clearAuth()
      return false
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

async function request<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const res = await fetch(BASE + path, {
    method,
    headers: headers(),
    signal,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  })

  if (res.status === 401 && !path.startsWith('/auth/')) {
    const refreshed = await tryRefresh()
    if (refreshed) {
      const retry = await fetch(BASE + path, {
        method,
        headers: headers(),
        signal,
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      })
      if (!retry.ok) {
        const err = await retry.json().catch(() => ({ error: retry.statusText }))
        throw new Error((err as { error?: string }).error || retry.statusText)
      }
      return retry.json() as Promise<T>
    } else {
      const { toastError } = await import('./toast')
      toastError('Session expired')
      window.location.href = '/login'
      throw new Error('Session expired')
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as { error?: string }).error || res.statusText)
  }
  return res.json() as Promise<T>
}

async function requestForm<T>(method: string, path: string, form: FormData, signal?: AbortSignal): Promise<T> {
  const authHeaders: Record<string, string> = {
    ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
  }
  const res = await fetch(BASE + path, {
    method,
    headers: authHeaders,
    body: form,
    signal,
  })
  if (res.status === 401 && !path.startsWith('/auth/')) {
    const refreshed = await tryRefresh()
    if (refreshed) {
      const retry = await fetch(BASE + path, {
        method,
        headers: { ...(token() ? { Authorization: `Bearer ${token()}` } : {}) },
        body: form,
        signal,
      })
      if (!retry.ok) {
        const err = await retry.json().catch(() => ({ error: retry.statusText }))
        throw new Error((err as { error?: string }).error || retry.statusText)
      }
      return retry.json() as Promise<T>
    }
    const { toastError } = await import('./toast')
    toastError('Session expired')
    window.location.href = '/login'
    throw new Error('Session expired')
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as { error?: string }).error || res.statusText)
  }
  return res.json() as Promise<T>
}

function filenameFromContentDisposition(value: string | null): string {
  if (!value) return 'document'
  const quoted = value.match(/filename="([^"]+)"/i)
  if (quoted?.[1]) return quoted[1]
  const plain = value.match(/filename=([^;]+)/i)
  return plain?.[1]?.trim() || 'document'
}

async function requestBlob(path: string, signal?: AbortSignal): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(BASE + path, {
    method: 'GET',
    headers: headers({ Accept: 'application/octet-stream' }),
    signal,
  })

  if (res.status === 401 && !path.startsWith('/auth/')) {
    const refreshed = await tryRefresh()
    if (refreshed) {
      const retry = await fetch(BASE + path, {
        method: 'GET',
        headers: headers({ Accept: 'application/octet-stream' }),
        signal,
      })
      if (!retry.ok) {
        const err = await retry.json().catch(() => ({ error: retry.statusText }))
        throw new Error((err as { error?: string }).error || retry.statusText)
      }
      return {
        blob: await retry.blob(),
        filename: filenameFromContentDisposition(retry.headers.get('Content-Disposition')),
      }
    } else {
      const { toastError } = await import('./toast')
      toastError('Session expired')
      window.location.href = '/login'
      throw new Error('Session expired')
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as { error?: string }).error || res.statusText)
  }
  return {
    blob: await res.blob(),
    filename: filenameFromContentDisposition(res.headers.get('Content-Disposition')),
  }
}

export const api = {
  auth: {
    login: (email: string, password: string) =>
      request<{ access_token: string; refresh_token: string; user: User }>('POST', '/auth/login', { email, password }),
  },
  deals: {
    list: (signal?: AbortSignal) => request<{ deals: Deal[] }>('GET', '/deals', undefined, signal),
    get: (id: string) => request<Deal>('GET', `/deals/${id}`),
    snapshot: (id: string, signal?: AbortSignal) => request<DealSnapshot>('GET', `/deals/${id}/snapshot`, undefined, signal),
    create: (data: DealWritePayload) => request<Deal>('POST', '/deals', data),
    update: (id: string, data: DealWritePayload) => request<Deal>('PATCH', `/deals/${id}`, data),
    advance: (id: string, data?: { target?: string; note?: string }) =>
      request<{ deal_id: string; stage: string; status: string }>('POST', `/deals/${id}/advance`, data ?? {}),
    delete: (id: string) => request<void>('DELETE', `/deals/${id}`),
  },
  portal: {
    createLink: (dealId: string) => request<PortalLink>('POST', `/deals/${dealId}/portal-link`, {}),
    status: (token: string, signal?: AbortSignal) =>
      request<PortalStatus>('GET', `/portal/${token}`, undefined, signal),
  },
  parties: {
    list: (signal?: AbortSignal) => request<{ parties: Party[] }>('GET', '/parties', undefined, signal),
    create: (data: Partial<Party>) => request<Party>('POST', '/parties', data),
    resolve: (data: Partial<Party>) => request<PartyResolveResult>('POST', '/parties/resolve', data),
    merge: (data: { keep_id: string; merge_id: string }) =>
      request<{ kept: Party; merged_id: string }>('POST', '/parties/merge', data),
    update: (id: string, data: Partial<Party>) => request<Party>('PATCH', `/parties/${id}`, data),
  },
  compliance: {
    reviewFairHousing: (data: { text: string; context?: string }) =>
      request<FairHousingReviewResult>('POST', '/compliance/fair-housing-review', data),
    listFairHousingTemplates: (signal?: AbortSignal) =>
      request<{ templates: FairHousingTemplate[] }>('GET', '/compliance/fair-housing-templates', undefined, signal),
  },
  dealParties: {
    list: (dealId: string) => request<{ parties: SnapshotParty[] }>('GET', `/deals/${dealId}/parties`),
    link: (dealId: string, data: { party_id: string; role: string; is_primary?: boolean }) =>
      request<SnapshotParty>('POST', `/deals/${dealId}/parties`, data),
    unlink: (dealId: string, linkId: string) => request<{ id: string }>('DELETE', `/deals/${dealId}/parties/${linkId}`),
  },
  deadlines: {
    list: (dealId: string) => request<{ deadlines: Deadline[] }>('GET', `/deals/${dealId}/deadlines`),
    generate: (dealId: string, negotiatedDays?: Record<string, number>) =>
      request<{ deadlines: Deadline[]; count: number }>('POST', `/deals/${dealId}/deadlines/generate`, { negotiated_days: negotiatedDays }),
    update: (dealId: string, dlId: string, data: Partial<Deadline>) =>
      request<Deadline>('PATCH', `/deals/${dealId}/deadlines/${dlId}`, data),
  },
  counterOffers: {
    list: (dealId: string) => request<{ counter_offers: CounterOffer[] }>('GET', `/deals/${dealId}/counter-offers`),
    create: (dealId: string, data: CounterOfferWritePayload) =>
      request<CounterOffer>('POST', `/deals/${dealId}/counter-offers`, data),
    signers: (dealId: string, counterOfferId: string) =>
      request<{ signers: DealSigner[] }>('GET', `/deals/${dealId}/counter-offers/${counterOfferId}/signers`),
    addSigner: (dealId: string, counterOfferId: string, data: Partial<DealSigner>) =>
      request<DealSigner>('POST', `/deals/${dealId}/counter-offers/${counterOfferId}/signers`, data),
    updateSigner: (dealId: string, counterOfferId: string, signerId: string, data: Partial<DealSigner>) =>
      request<DealSigner>('PATCH', `/deals/${dealId}/counter-offers/${counterOfferId}/signers/${signerId}`, data),
    deleteSigner: (dealId: string, counterOfferId: string, signerId: string) =>
      request<void>('DELETE', `/deals/${dealId}/counter-offers/${counterOfferId}/signers/${signerId}`),
    update: (dealId: string, coId: string, data: CounterOfferWritePayload) =>
      request<CounterOffer>('PATCH', `/deals/${dealId}/counter-offers/${coId}`, data),
    delete: (dealId: string, coId: string) => request<void>('DELETE', `/deals/${dealId}/counter-offers/${coId}`),
    ratify: (dealId: string, counterOfferId: string, negotiatedDays?: Record<string, number>) =>
      request<{
        deal_id: string
        counter_offer_id: string
        ratification_date: string
        deadlines: Deadline[]
        cost_allocations: CostAllocation[]
        documents: Document[]
        tasks: Task[]
        loan?: LoanTracking
      }>('POST', `/deals/${dealId}/counter-offers/${counterOfferId}/ratify`, { negotiated_days: negotiatedDays }),
    reject: (dealId: string, counterOfferId: string, data?: { rejected_at?: string; note?: string }) =>
      request<CounterOffer>('POST', `/deals/${dealId}/counter-offers/${counterOfferId}/reject`, data ?? {}),
  },
  costAllocations: {
    list: (dealId: string) => request<{ cost_allocations: CostAllocation[] }>('GET', `/deals/${dealId}/cost-allocations`),
    generate: (dealId: string) =>
      request<{ cost_allocations: CostAllocation[]; count: number }>('POST', `/deals/${dealId}/cost-allocations/generate`, {}),
    update: (dealId: string, allocationId: string, data: Partial<CostAllocation>) =>
      request<CostAllocation>('PATCH', `/deals/${dealId}/cost-allocations/${allocationId}`, data),
  },
  documents: {
    listAll: (signal?: AbortSignal) => request<{ documents: RepositoryDocument[] }>('GET', '/documents', undefined, signal),
    download: (documentId: string) => requestBlob(`/documents/${documentId}/download`),
    list: (dealId: string) => request<{ documents: Document[] }>('GET', `/deals/${dealId}/documents`),
    create: (dealId: string, data: Partial<Document>) => request<Document>('POST', `/deals/${dealId}/documents`, data),
    update: (dealId: string, documentId: string, data: Partial<Document>) =>
      request<Document>('PATCH', `/deals/${dealId}/documents/${documentId}`, data),
    deliver: (dealId: string, documentId: string, data?: { delivered_at?: string }) =>
      request<Document>('POST', `/deals/${dealId}/documents/${documentId}/deliver`, data ?? {}),
    sign: (dealId: string, documentId: string, signerId: string, data?: { signed_at?: string; signed_by_name?: string }) =>
      request<Document>('POST', `/deals/${dealId}/documents/${documentId}/signers/${signerId}/sign`, data ?? {}),
    initial: (dealId: string, documentId: string, signerId: string, data?: { initialed_at?: string; initialed_by_name?: string }) =>
      request<Document>('POST', `/deals/${dealId}/documents/${documentId}/signers/${signerId}/initial`, data ?? {}),
    view: (dealId: string, documentId: string, data?: { viewed_at?: string; date?: string }) =>
      request<Document>('POST', `/deals/${dealId}/documents/${documentId}/view`, data ?? {}),
    markReturned: (dealId: string, documentId: string, data?: { returned_at?: string; date?: string }) =>
      request<Document>('POST', `/deals/${dealId}/documents/${documentId}/return`, data ?? {}),
    file: (dealId: string, documentId: string, data?: { filed_at?: string; date?: string }) =>
      request<Document>('POST', `/deals/${dealId}/documents/${documentId}/file`, data ?? {}),
    generateChecklist: (dealId: string) =>
      request<{ documents: Document[]; count: number; rendered?: number; render_skipped?: number; render_error?: string }>(
        'POST',
        `/deals/${dealId}/documents/generate-checklist`,
        {},
      ),
    render: (dealId: string, documentId: string) =>
      request<Document>('POST', `/deals/${dealId}/documents/${documentId}/render`, {}),
    auditExecution: (dealId: string, data?: { as_of?: string }) =>
      request<ExecutionAuditResult>('POST', `/deals/${dealId}/documents/audit-execution`, data ?? {}),
    recordKnownDefectIssue: (dealId: string, data?: { discovered_at?: string; description?: string }) =>
      request<KnownDefectIssueResult>('POST', `/deals/${dealId}/disclosures/known-defect`, data ?? {}),
  },
  documentLayouts: {
    list: () => request<{ layouts: DocumentLayout[] }>('GET', '/admin/document-layouts'),
    get: (id: string) => request<DocumentLayout>('GET', `/admin/document-layouts/${id}`),
    activate: (id: string) => request<DocumentLayout>('POST', `/admin/document-layouts/${id}/activate`, {}),
    update: (id: string, data: { field_map: DocumentLayoutFieldMap; name?: string }) =>
      request<DocumentLayout>('PATCH', `/admin/document-layouts/${id}`, data),
    create: async (input: { form_code: string; name?: string; activate?: boolean; file: File; field_map?: DocumentLayoutFieldMap }) => {
      const form = new FormData()
      form.append('form_code', input.form_code)
      if (input.name) form.append('name', input.name)
      if (input.activate) form.append('activate', 'true')
      if (input.field_map) form.append('field_map', JSON.stringify(input.field_map))
      form.append('file', input.file)
      return requestForm<DocumentLayout>('POST', '/admin/document-layouts', form)
    },
    inspect: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      return requestForm<{ fields: DocumentLayoutField[]; count: number }>('POST', '/admin/document-layouts/inspect', form)
    },
  },
  tasks: {
    list: (dealId: string) => request<{ tasks: Task[] }>('GET', `/deals/${dealId}/tasks`),
    mine: (signal?: AbortSignal) => request<{ tasks: Task[] }>('GET', '/me/tasks', undefined, signal),
    create: (dealId: string, data: Partial<Task>) => request<Task>('POST', `/deals/${dealId}/tasks`, data),
    update: (dealId: string, taskId: string, data: TaskUpdateInput) =>
      request<Task>('PATCH', `/deals/${dealId}/tasks/${taskId}`, data),
    delete: (dealId: string, taskId: string) => request<void>('DELETE', `/deals/${dealId}/tasks/${taskId}`),
  },
  inspections: {
    list: (dealId: string) => request<{ inspections: Inspection[] }>('GET', `/deals/${dealId}/inspections`),
    create: (dealId: string, data: Partial<Inspection>) =>
      request<Inspection>('POST', `/deals/${dealId}/inspections`, data),
    update: (dealId: string, inspectionId: string, data: Partial<Inspection>) =>
      request<Inspection>('PATCH', `/deals/${dealId}/inspections/${inspectionId}`, data),
  },
  repairs: {
    list: (dealId: string) => request<{ repair_requests: RepairRequest[] }>('GET', `/deals/${dealId}/repair-requests`),
    create: (dealId: string, data: Partial<RepairRequest>) =>
      request<RepairRequest>('POST', `/deals/${dealId}/repair-requests`, data),
    update: (dealId: string, repairId: string, data: Partial<RepairRequest>) =>
      request<RepairRequest>('PATCH', `/deals/${dealId}/repair-requests/${repairId}`, data),
  },
  loan: {
    get: (dealId: string) => request<LoanTracking>('GET', `/deals/${dealId}/loan`),
    upsert: (dealId: string, data: Partial<LoanTracking>) => request<LoanTracking>('PUT', `/deals/${dealId}/loan`, data),
  },
  wire: {
    list: (dealId: string) => request<{ wire_instructions: WireInstruction[] }>('GET', `/deals/${dealId}/wire`),
    deliver: (dealId: string, data: Partial<WireInstruction>) =>
      request<WireInstruction>('POST', `/deals/${dealId}/wire`, data),
    ack: (dealId: string, wireId: string, data: { acknowledged_by?: string }) =>
      request<WireInstruction>('POST', `/deals/${dealId}/wire/${wireId}/ack`, data),
  },
  escrow: {
    confirmEarnestMoney: (dealId: string, data?: { received_at?: string; note?: string }) =>
      request<EarnestMoneyResult>('POST', `/deals/${dealId}/escrow/earnest-money`, data ?? {}),
    confirmProofOfFunds: (dealId: string, data?: { confirmed_at?: string; note?: string }) =>
      request<ProofOfFundsResult>('POST', `/deals/${dealId}/escrow/proof-of-funds`, data ?? {}),
    confirmInstructions: (dealId: string, data?: { returned_at?: string; note?: string }) =>
      request<EscrowInstructionsResult>('POST', `/deals/${dealId}/escrow/instructions`, data ?? {}),
  },
  notices: {
    list: (dealId: string) => request<{ notices: PerformanceNotice[] }>('GET', `/deals/${dealId}/notices`),
    create: (dealId: string, data: Partial<PerformanceNotice>) =>
      request<PerformanceNotice>('POST', `/deals/${dealId}/notices`, data),
    update: (dealId: string, noticeId: string, data: Partial<PerformanceNotice>) =>
      request<PerformanceNotice>('PATCH', `/deals/${dealId}/notices/${noticeId}`, data),
    removeContingencies: (
      dealId: string,
      data: { deadline_ids?: string[]; types?: string[]; remove_all?: boolean; removed_at?: string; cr_b_document_id?: string },
    ) => request<{ deal_id: string; removed_at: string }>('POST', `/deals/${dealId}/contingencies/remove`, data),
  },
  notifications: {
    mine: (signal?: AbortSignal) => request<{ notifications: Notification[] }>('GET', '/me/notifications', undefined, signal),
    list: (dealId: string) => request<{ notifications: Notification[] }>('GET', `/deals/${dealId}/notifications`),
  },
  milestones: {
    signing: (dealId: string, data?: { date?: string; note?: string }) =>
      request<MilestoneResult>('POST', `/deals/${dealId}/milestones/signing`, data ?? {}),
    funding: (dealId: string, data?: { date?: string; note?: string }) =>
      request<MilestoneResult>('POST', `/deals/${dealId}/milestones/funding`, data ?? {}),
    recording: (dealId: string, data?: { date?: string; note?: string }) =>
      request<MilestoneResult>('POST', `/deals/${dealId}/milestones/recording`, data ?? {}),
    keys: (dealId: string, data?: { date?: string; note?: string }) =>
      request<MilestoneResult>('POST', `/deals/${dealId}/milestones/keys`, data ?? {}),
    mls: (dealId: string, data?: { date?: string; note?: string }) =>
      request<MilestoneResult>('POST', `/deals/${dealId}/milestones/mls`, data ?? {}),
    compliance: (dealId: string, data?: { date?: string; note?: string }) =>
      request<MilestoneResult>('POST', `/deals/${dealId}/milestones/compliance`, data ?? {}),
    disbursement: (dealId: string, data?: { date?: string; note?: string }) =>
      request<MilestoneResult>('POST', `/deals/${dealId}/milestones/disbursement`, data ?? {}),
    complete: (dealId: string, milestone: ClosingMilestone, data?: { date?: string; note?: string }) =>
      request<MilestoneResult>('POST', `/deals/${dealId}/milestones/${milestone}`, data ?? {}),
  },
  closing: {
    completeFinalWalkthrough: (dealId: string, data?: { completed_at?: string; note?: string }) =>
      request<FinalWalkthroughResult>('POST', `/deals/${dealId}/closing/final-walkthrough`, data ?? {}),
    confirmTenantRemoval: (dealId: string, data?: { confirmed_at?: string; note?: string }) =>
      request<TenantRemovalResult>('POST', `/deals/${dealId}/closing/tenant-removal`, data ?? {}),
    recordTenantRemovalBlocked: (dealId: string, data?: { blocked_at?: string; note?: string }) =>
      request<TenantRemovalBlockedResult>('POST', `/deals/${dealId}/closing/tenant-removal/blocked`, data ?? {}),
    completePostClosePackage: (dealId: string, data?: { completed_at?: string; note?: string }) =>
      request<PostClosePackageResult>('POST', `/deals/${dealId}/closing/post-close-package`, data ?? {}),
  },
}
